import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Col, Row, Statistic, Typography, Space, Tag, Spin } from "antd";
import {
  MessageOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FireOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  SmileOutlined,
  FrownOutlined,
  MehOutlined,
} from "@ant-design/icons";
import { API } from "@/providers/data";

const { Title, Text } = Typography;

type DashboardMetrics = {
  overview: {
    total_reviews: number;
    without_response: number;
    published: number;
    coverage_percent: number;
  };
  processing_status: {
    pending: number;
    enriched: number;
    response_pending: number;
    response_draft: number;
    response_approved: number;
  };
  time_metrics: {
    reviews_last_24h: number;
    reviews_last_7d: number;
    avg_time_to_response_hours: number;
  };
  routing_stats: Array<{
    rule_name: string;
    count: number;
    mode: string;
  }>;
  sentiment: {
    positive: number;
    negative: number;
    neutral: number;
  };
  critical: {
    stop_words: number;
    low_rating_no_response: number;
    questions_no_response: number;
  };
  ai_metrics: {
    ai_generated: number;
    template_generated: number;
    manual: number;
    ai_percent: number;
  };
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API}/api/v1/dashboard/metrics`, { headers });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      const data = await res.json();
      setMetrics(data);
    } catch (err: any) {
      console.error('Failed to fetch dashboard metrics:', err);
      setError(err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !metrics) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Загрузка метрик...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#ff4d4f' }}>
          Ошибка загрузки: {error}
        </div>
      </Card>
    );
  }

  if (!metrics) return null;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", padding: "8px 0" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Главная
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <SyncOutlined spin={loading} /> Обновляется каждые 30 сек
        </Text>
      </div>

      {/* Блок 1: Основные метрики */}
      <Card title="Отзывы - основное" size="small">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Всего отзывов"
                value={metrics.overview.total_reviews}
                prefix={<MessageOutlined />}
                valueStyle={{ color: "#1677ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?response_status=null')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Без ответа"
                value={metrics.overview.without_response}
                prefix={<ClockCircleOutlined />}
                valueStyle={{
                  color: metrics.overview.without_response > 50 ? "#faad14" : "#52c41a"
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?response_status=published')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Опубликовано"
                value={metrics.overview.published}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card bordered={false}>
              <Statistic
                title="% покрытие"
                value={metrics.overview.coverage_percent}
                precision={1}
                suffix="%"
                prefix={<FileTextOutlined />}
                valueStyle={{
                  color: metrics.overview.coverage_percent > 60 ? "#52c41a" : "#faad14"
                }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Блок 2: Статусы обработки */}
      <Card title="Статусы обработки" size="small">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?status=PENDING')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Ждут enrichment"
                value={metrics.processing_status.pending}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?status=ENRICHED')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Обогащены"
                value={metrics.processing_status.enriched}
                prefix={<ThunderboltOutlined />}
                valueStyle={{ color: "#1677ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?response_status=pending')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Ждут утверждения"
                value={metrics.processing_status.response_pending}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?response_status=draft')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Черновики"
                value={metrics.processing_status.response_draft}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: "#13c2c2" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?response_status=approved')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Утверждены"
                value={metrics.processing_status.response_approved}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Блок 3: Критичное */}
      <Card title="Требуют внимания" size="small">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?has_stop_words=true&response_status=null')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title={
                  <Space>
                    <Text>Стоп-слова</Text>
                    {metrics.critical.stop_words > 0 && <Tag color="red">Критично!</Tag>}
                  </Space>
                }
                value={metrics.critical.stop_words}
                prefix={<WarningOutlined />}
                valueStyle={{
                  color: metrics.critical.stop_words > 0 ? "#ff4d4f" : "#52c41a"
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?rating=1-2&response_status=null')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title={
                  <Space>
                    <Text>1-2★ без ответа</Text>
                    {metrics.critical.low_rating_no_response > 3 && <Tag color="red">Срочно!</Tag>}
                  </Space>
                }
                value={metrics.critical.low_rating_no_response}
                prefix={<FireOutlined />}
                valueStyle={{
                  color: metrics.critical.low_rating_no_response > 3 ? "#ff4d4f" : "#faad14"
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?has_question_mark=true&response_status=null')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Вопросы без ответа"
                value={metrics.critical.questions_no_response}
                prefix={<QuestionCircleOutlined />}
                valueStyle={{
                  color: metrics.critical.questions_no_response > 5 ? "#faad14" : "#52c41a"
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card bordered={false}>
              <Statistic
                title="% AI генерация"
                value={metrics.ai_metrics.ai_percent}
                precision={1}
                suffix="%"
                prefix={<RobotOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Блок 4: Тренды */}
      <Card title="Тренды" size="small">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false}>
              <Statistic
                title="За последние 24 часа"
                value={metrics.time_metrics.reviews_last_24h}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#1677ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false}>
              <Statistic
                title="За последние 7 дней"
                value={metrics.time_metrics.reviews_last_7d}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#1677ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false}>
              <Statistic
                title="Среднее время до ответа"
                value={metrics.time_metrics.avg_time_to_response_hours}
                precision={1}
                suffix="ч"
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Блок 5: Sentiment анализ */}
      <Card title="Sentiment анализ" size="small">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?sentiment=positive')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Позитивные"
                value={metrics.sentiment.positive}
                prefix={<SmileOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?sentiment=neutral')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Нейтральные"
                value={metrics.sentiment.neutral}
                prefix={<MehOutlined />}
                valueStyle={{ color: "#1677ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card
              bordered={false}
              hoverable
              onClick={() => navigate('/reviews?sentiment=negative')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Негативные"
                value={metrics.sentiment.negative}
                prefix={<FrownOutlined />}
                valueStyle={{ color: "#ff4d4f" }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Блок 6: Эффективность маршрутизации */}
      <Card title="Эффективность маршрутизации" size="small">
        <Row gutter={[16, 16]}>
          {metrics.routing_stats.slice(0, 6).map((stat, idx) => (
            <Col xs={24} sm={12} md={8} key={idx}>
              <Card
                bordered={false}
                hoverable
                onClick={() => navigate('/strategies')}
                style={{ cursor: 'pointer' }}
              >
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Text strong style={{ fontSize: 13 }}>{stat.rule_name}</Text>
                  <Space>
                    <Statistic
                      value={stat.count}
                      valueStyle={{ fontSize: 24 }}
                    />
                    <Text type="secondary">отзывов</Text>
                  </Space>
                  <Tag color={
                    stat.mode === 'ai' ? 'purple' :
                    stat.mode === 'template' ? 'blue' :
                    'orange'
                  }>
                    {stat.mode || 'unknown'}
                  </Tag>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
        {metrics.routing_stats.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#8c8c8c' }}>
            Нет данных по маршрутизации
          </div>
        )}
      </Card>

      {/* Блок 7: AI метрики */}
      <Card title="AI метрики генерации" size="small">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card bordered={false}>
              <Statistic
                title="AI генерация"
                value={metrics.ai_metrics.ai_generated}
                prefix={<RobotOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card bordered={false}>
              <Statistic
                title="По шаблонам"
                value={metrics.ai_metrics.template_generated}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: "#1677ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card bordered={false}>
              <Statistic
                title="Отредактировано"
                value={metrics.ai_metrics.manual}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card bordered={false}>
              <Statistic
                title="% AI автоматизации"
                value={metrics.ai_metrics.ai_percent}
                precision={1}
                suffix="%"
                prefix={<RobotOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>
      </Card>
    </Space>
  );
}
